import { maxSizeMB } from './../config/constants.js';
import validationClasses from './../config/validationClasses.js';
import * as util from './utility.js';
/**
 * フォームのバリデーションおよび関連する設定を管理するクラス
 * @class FormValidator
 */
export default class FormValidator {
    /**
     * コンストラクタ
     * @param {object.<string, ValidationRule>} validationConfig - バリデーションルールの設定オブジェクト
     * @param {object.<string, string>} noInputConfig - 入力不備時のエラーメッセージ設定オブジェクト
     */
    constructor(validationConfig, noInputConfig) {
        var _a, _b;
        this.form = document.querySelector('form');
        this.validationConfig = validationConfig;
        this.noInputConfig = noInputConfig;
        this.uploadFields = (_b = (_a = this.form) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`.${this.validationConfig.file.check}`)) !== null && _b !== void 0 ? _b : document.querySelectorAll('');
    }
    /**
     * 入力フィールドに対して必須チェックするメソッド
     * @method requiredValidator
     * @param {HTMLElement | HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    requiredValidator(field) {
        if (field.tagName === 'SELECT') {
            const selectField = field;
            if (selectField.selectedIndex === 0) {
                return { err: 'requiredSelect', through: 0 };
            }
        }
        else if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
            const inputField = field;
            if (inputField.value.trim() === '') {
                if (inputField.hasAttribute('readonly')) {
                    return { err: 'requiredSelect', through: 0 };
                }
                return { err: 'required', through: 0 };
            }
        }
        else {
            const inputs = field.querySelectorAll('input[type="radio"], input[type="checkbox"]');
            const isChecked = Array.from(inputs).some(input => input.checked);
            if (!isChecked) {
                return { err: 'requiredSelect', through: 0 };
            }
        }
        return { err: '', through: 1 };
    }
    /**
     * 子要素の中で少なくとも1つの入力があるかチェックするメソッド
     * @method anyCheckValidator
     * @param {HTMLElement} container - 子要素を含むコンテナ要素
     * @returns { err: string, through: number } - エラー情報
     */
    anyRequiredValidator(container) {
        const fields = container.querySelectorAll('input, input[type="radio"], input[type="checkbox"], textarea, select');
        const results = [];
        for (const field of fields) {
            const result = this.requiredValidator(field);
            results.push(result);
        }
        const hasRequiredError = results.some(result => result.through === 1);
        return hasRequiredError ? { err: '', through: 1 } : { err: 'anyRequired', through: 0 };
    }
    /**
     * 共通のバリデーションメソッド
     * @param {HTMLInputElement} field - 入力フィールド
     * @param {string} value - 前処理された値
     * @param {keyof typeof validationConfig} key - バリデーション設定のキー
     * @returns { err: string, through: number } - エラー情報
     */
    validateField(field, value, key) {
        var _a, _b;
        const config = this.validationConfig[key];
        if (value && field.classList.contains((_a = config.check) !== null && _a !== void 0 ? _a : '')) {
            if (!((_b = config.regex) === null || _b === void 0 ? void 0 : _b.test(value))) {
                return { err: key, through: 0 };
            }
        }
        return { err: '', through: 1 };
    }
    /**
     * 電話番号フィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    telValidator(field) {
        let checkValue = util.toHalfWidth(field.value.trim());
        checkValue = checkValue.replace(/-/g, '');
        return this.validateField(field, checkValue, 'tel');
    }
    /**
     * メールアドレスフィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    mailValidator(field) {
        const checkValue = util.toHalfWidth(field.value.trim());
        return this.validateField(field, checkValue, 'mail');
    }
    /**
     * 数値フィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    numValidator(field) {
        const checkValue = util.toHalfWidth(field.value.trim());
        return this.validateField(field, checkValue, 'num');
    }
    /**
     * アルファベットフィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    alphabetValidator(field) {
        const checkValue = util.toHalfWidth(field.value.trim());
        return this.validateField(field, checkValue, 'alphabet');
    }
    /**
     * 英数字フィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    alphaNumValidator(field) {
        const checkValue = util.toHalfWidth(field.value.trim());
        return this.validateField(field, checkValue, 'alphaNum');
    }
    /**
     * 郵便番号フィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    zipValidator(field) {
        if (field.classList.contains(validationClasses.addrErr))
            return { err: 'addr', through: 0 };
        let checkValue = util.toHalfWidth(field.value.trim());
        checkValue = checkValue.replace(/-/g, '');
        return this.validateField(field, checkValue, 'zip');
    }
    /**
     * カタカナ・ひらがなフィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    kanaValidator(field) {
        const checkValue = field.value.trim();
        return this.validateField(field, checkValue, 'kana');
    }
    /**
     * ひらがなフィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    hiraganaValidator(field) {
        const checkValue = field.value.trim();
        return this.validateField(field, checkValue, 'hiragana');
    }
    /**
     * カタカナフィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    katakanaValidator(field) {
        const checkValue = field.value.trim();
        return this.validateField(field, checkValue, 'katakana');
    }
    /**
     * ファイルフィールドのバリデーション
     * @param {HTMLInputElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    fileValidator(field) {
        var _a, _b;
        const checkValue = field.value.trim();
        if (checkValue === '')
            return { err: '', through: 1 };
        if ((_a = this.form) === null || _a === void 0 ? void 0 : _a.classList.contains((_b = this.validationConfig.upload.check) !== null && _b !== void 0 ? _b : '')) {
            const uploadValidation = this.uploadValidator();
            if (uploadValidation.err) {
                return uploadValidation;
            }
        }
        return this.validateField(field, checkValue, 'file');
    }
    /**
     * フォーム全体でアップロード上限に達していないかのバリデーション
     * @returns { err: string, through: number } - エラー情報
     */
    uploadValidator() {
        var _a, _b, _c;
        this.uploadFields = (_b = (_a = this.form) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`.${this.validationConfig.file.check}`)) !== null && _b !== void 0 ? _b : document.querySelectorAll('');
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        let totalSize = 0;
        (_c = this.uploadFields) === null || _c === void 0 ? void 0 : _c.forEach(uploadField => {
            if (uploadField.files) {
                Array.from(uploadField.files).forEach(file => {
                    totalSize += file.size;
                });
            }
        });
        return (totalSize > maxSizeBytes) ? { err: 'upload', through: 0 } : { err: '', through: 1 };
    }
    /**
     * すべてのバリデーションをチェックするメソッド
     * @param {HTMLElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field - 入力フィールド
     * @returns { err: string, through: number } - エラー情報
     */
    allCheckValidator(field) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const validators = {
            [(_a = this.validationConfig.required.check) !== null && _a !== void 0 ? _a : '']: this.requiredValidator,
            [(_b = this.validationConfig.tel.check) !== null && _b !== void 0 ? _b : '']: this.telValidator,
            [(_c = this.validationConfig.mail.check) !== null && _c !== void 0 ? _c : '']: this.mailValidator,
            [(_d = this.validationConfig.num.check) !== null && _d !== void 0 ? _d : '']: this.numValidator,
            [(_e = this.validationConfig.alphabet.check) !== null && _e !== void 0 ? _e : '']: this.alphabetValidator,
            [(_f = this.validationConfig.alphaNum.check) !== null && _f !== void 0 ? _f : '']: this.alphaNumValidator,
            [(_g = this.validationConfig.zip.check) !== null && _g !== void 0 ? _g : '']: this.zipValidator,
            [(_h = this.validationConfig.kana.check) !== null && _h !== void 0 ? _h : '']: this.kanaValidator,
            [(_j = this.validationConfig.hiragana.check) !== null && _j !== void 0 ? _j : '']: this.hiraganaValidator,
            [(_k = this.validationConfig.katakana.check) !== null && _k !== void 0 ? _k : '']: this.katakanaValidator,
            [(_l = this.validationConfig.file.check) !== null && _l !== void 0 ? _l : '']: this.fileValidator,
        };
        for (const className in validators) {
            if (field.classList.contains(className)) {
                const validationFlag = validators[className].call(this, field);
                if (validationFlag.err !== '') {
                    return validationFlag;
                }
            }
        }
        return { err: '', through: 1 };
    }
}
//# sourceMappingURL=formValidator.js.map